import { DEFAULT_MEASUREMENT } from "@utils/default";
import { useFormatter, useTranslations } from "next-intl";
import React, { ComponentPropsWithRef, Dispatch, forwardRef, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ControllerFieldState, ControllerRenderProps, set } from "react-hook-form";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css";
import { debounce } from "throttle-debounce";

import CurrencyIcon from "@/components/ui/CurrencyIcon/CurrencyIcon";
import FilterAction from "@/components/ui/Form/Filters/FilterAction";
import "@/components/ui/Form/Filters/components/Range/range.css";
import { UserContext } from "@/context/UserContext";
import { filterSelectionTracking } from "@/dataLayer/dataLayer";

export interface FilterRangeBaseProps extends ComponentPropsWithRef<"input"> {
  name: string;
  reverse?: boolean;
  hideBorder?: boolean;
  hideLabel?: boolean;
  label: string;
  filter: string;
  displayInSearchBar?: boolean;
  isSearchpage?: boolean;
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  values?: number[];
  availableValues?: number[];
}
const FilterRangeBase = forwardRef<HTMLInputElement, FilterRangeBaseProps>(
  ({ name, filter, displayInSearchBar = true, field, fieldState, reverse, isSearchpage, values, availableValues, ...rest }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const t = useTranslations();
    const format = useFormatter();
    const { currency, measurement } = useContext(UserContext);
    const [userValues, setUserValues] = useState<number[]>(field?.value ?? values);
    const [inputValues, setInputValues] = useState<{ min: string | undefined; max: string | undefined }>({ min: undefined, max: undefined });

    useEffect(() => {
      if (values && (!field?.value || field.value.length === 0)) {
        setUserValues([values[0] || 0, values[1] || values[0] || 0]);
      }
    }, [availableValues, values, field]);

    const textValues = useMemo(() => {
      let minValue = "";
      let maxValue = "";
      switch (filter) {
        case "price":
          minValue = format.number(Math.floor(userValues[0] / 100), {
            style: "currency",
            currency: currency,
            trailingZeroDisplay: "stripIfInteger",
          });
          maxValue = format.number(Math.floor(userValues[1] / 100), {
            style: "currency",
            currency: currency,
            trailingZeroDisplay: "stripIfInteger",
          });

          break;
        case "area":
          minValue = `${format.number(Math.floor(userValues[0]))}`;
          maxValue = `${format.number(Math.floor(userValues[1]))}`;
          break;
      }
      return [minValue, maxValue];
    }, [userValues, currency, measurement]);

    const debounced = debounce(100, (range: number[]) => {
      field?.onChange(range);
    });

    const step = useMemo(() => {
      if (values && values.length === 2) {
        switch (filter) {
          case "price":
            return 10;
          case "area":
            return 10;
          default:
            return 1;
        }
      }
    }, [filter, values]);

    const handleminprice = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setInputValues((prev) => ({ ...prev, min: e.target.value.replaceAll(/,/g, "") }));
    };

    const handleMaxPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setInputValues((prev) => ({ ...prev, max: e.target.value.replaceAll(/,/g, "") }));
    };

    const validateMinPrice = (e: React.FocusEvent<HTMLInputElement>) => {
      const cleanValue = e.target.value.replace(/[^0-9]/g, "");

      if (!cleanValue) {
        setInputValues((prev) => ({ ...prev, min: undefined }));
        return;
      }

      let minValue: number;
      if (filter === "price") {
        const numberValue = Number(cleanValue);
        minValue = Math.floor(numberValue * 100);
      } else {
        minValue = parseInt(cleanValue);
      }

      if (minValue < (values?.[0] || 0)) {
        setInputValues((prev) => ({ ...prev, min: undefined }));
        return;
      }

      const newMin = Math.min(minValue, userValues[1]);
      const newValues = [newMin, userValues[1]];

      setUserValues(newValues);
      debounced(newValues);
      setInputValues((prev) => ({ ...prev, min: undefined }));
    };

    const validateMaxPrice = (e: React.FocusEvent<HTMLInputElement>) => {
      const cleanValue = e.target.value.replace(/[^0-9]/g, "");

      if (!cleanValue) {
        setInputValues((prev) => ({ ...prev, max: undefined }));
        return;
      }

      let maxValue: number;
      if (filter === "price") {
        maxValue = Math.floor(Number(cleanValue) * 100);
      } else {
        maxValue = parseInt(cleanValue);
      }

      if (maxValue < userValues[0]) {
        setInputValues((prev) => ({ ...prev, max: undefined }));
        return;
      }

      const newMax = Math.min(maxValue, values?.[1] || maxValue);
      const newValues = [userValues[0], newMax];

      setUserValues(newValues);
      debounced(newValues);
      setInputValues((prev) => ({ ...prev, max: undefined }));
    };

    const handleFilteTracking = (values: any) => filterSelectionTracking("", "", "", values);

    // useEffect(() => {
    //   if (!values || values.length !== 2) return;

    //   setUserValues([values[0], values[1]]);
    //   setInputValues({ min: undefined, max: undefined });
    // }, [values, currency]);

    return (
      !!values &&
      values.length === 2 && (
        <>
          <div
            key={`range-${name}-${currency}-${measurement}`}
            ref={containerRef}
            className={`filter-range w-full ${displayInSearchBar ? "pt-m pb-m" : ""}`}
          >
            <div className={`${displayInSearchBar ? "w-full" : "md:w-4/5 mx-auto"}`}>
              <RangeSlider
                min={values[0]}
                max={values[1]}
                step={step}
                // value={[
                //   Math.max(userValues[0], availableValues?.[0] ?? userValues[0]),
                //   Math.min(userValues[1], availableValues?.[1] ?? userValues[1]),
                // ]}
                value={[userValues[0], userValues[1]]}
                onInput={(currentValues) => {
                  setUserValues([
                    Math.max(currentValues[0], values?.[0] ?? currentValues[0]),
                    Math.min(currentValues[1], values?.[1] ?? currentValues[1]),
                  ]);
                }}
                onRangeDragEnd={() => debounced(userValues)}
                onThumbDragEnd={() => debounced(userValues)}
              />
            </div>
            <div className={`flex justify-between ${displayInSearchBar ? "w-full mt-m min-w-[250px]" : "md:w-4/5 mx-auto mt-xl mb-l"}`}>
              <div className="filter-range-display w-fit flex flex-col border-b p-xs min-w-[47%] border-b-gray-20">
                <label className="py-xxs text-xs w-fit font-secondary font-secondary-light  filter-range-display" htmlFor={`range-${name}-min`}>
                  {t("form.label.minimum")}
                </label>
                <div className="flex items-start w-full">
                  {filter === "price" && typeof textValues[0] === "string" && (textValues[0].includes("AED") || textValues[0].includes("د.إ.")) && (
                    <span className="ml-1 mr-xxs flex items-center">
                      <CurrencyIcon color="black" />
                    </span>
                  )}
                  <input
                    name={`range-${name}-min`}
                    id={`range-${name}-min`}
                    className="text-s font-secondary block font-medium bg-transparent flex-grow-0"
                    type="text"
                    // value={inputValues.min || textValues[0]}
                    value={
                      inputValues.min !== undefined
                        ? inputValues.min
                        : filter === "price" && typeof textValues[0] === "string" && (textValues[0].includes("AED") || textValues[0].includes("د.إ."))
                          ? textValues[0].replace(/AED|د\.إ\./g, "").trim()
                          : textValues[0]
                    }
                    onChange={handleminprice}
                    onBlur={validateMinPrice}
                    style={
                      filter === "area"
                        ? {
                            width: `${Math.max(inputValues.min?.toString().length || textValues[0]?.toString().length || 0, 0) + 1}ch`,
                            minWidth: "10px",
                            marginRight: "2px",
                          }
                        : {}
                    }
                  />
                  {filter === "area" && (
                    <p className="text-s font-secondary font-medium bg-transparent w-full pl-3 ">
                      {measurement === DEFAULT_MEASUREMENT ? "SQFT" : "MQ"}
                    </p>
                  )}
                </div>
              </div>
              <div className="filter-range-display w-fit flex flex-col border-b p-xs  min-w-[47%] border-b-gray-20">
                <label className="py-xxs text-xs font-secondary w-fit font-secondary-light filter-range-display" htmlFor={`range-${name}-max`}>
                  {t("form.label.maximum")}
                </label>
                <div className="flex items-start w-full">
                  {filter === "price" && typeof textValues[1] === "string" && (textValues[1].includes("AED") || textValues[1].includes("د.إ.")) && (
                    <span className="ml-1 mr-xxs flex items-center">
                      <CurrencyIcon color="black" />
                    </span>
                  )}
                  <input
                    className="text-s font-secondary block font-medium bg-transparent w-full flex-grow-0"
                    id={`range-${name}-max`}
                    type="text"
                    // value={inputValues.max || textValues[1]}
                    value={
                      inputValues.max !== undefined
                        ? inputValues.max
                        : filter === "price" && typeof textValues[1] === "string" && (textValues[1].includes("AED") || textValues[1].includes("د.إ."))
                          ? textValues[1].replace(/AED|د\.إ\./g, "").trim()
                          : textValues[1]
                    }
                    onChange={handleMaxPrice}
                    onBlur={validateMaxPrice}
                    style={
                      filter === "area"
                        ? {
                            width: `${Math.max(inputValues.max?.toString().length || textValues[1]?.toString().length || 0, 0) + 2}ch`,
                            minWidth: "10px",
                            marginRight: "2px",
                          }
                        : {}
                    }
                  />

                  {filter === "area" && (
                    <p className="text-s font-secondary font-medium bg-transparent w-full pl-3 ">
                      {measurement === DEFAULT_MEASUREMENT ? "SQFT" : "MQ"}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {isSearchpage && <FilterAction setDisplayFilters={() => {}} />}
          </div>
        </>
      )
    );
  },
);
FilterRangeBase.displayName = "FilterRangeBase";
export default FilterRangeBase;
